import React, { useRef, useEffect } from "react";
import { Box, CircularProgress, Typography, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import LeadsCard from "./Leads.Card";
import LeadsPinnedGroup from "./Leads.PinnedGroup";

const LeadsList = ({
  leads,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  pendingActions,
  onAccept,
  onRelease,
  onClose,
  onReopen,
  onTransfer,
  onLeadDetails,
  onOpenConversation,
  onPin,
  onUnpin,
  onReorderPins,
  onFavorite,
  onUnfavorite,
  onManageLists,
  detailed,
}) => {
  const { t } = useTranslation();
  const observerTarget = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onFetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const target = observerTarget.current;
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [hasNextPage, isFetchingNextPage, onFetchNextPage]);

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (leads.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {t("leads.noLeadsFound")}
        </Typography>
      </Box>
    );
  }

  // As ações ficam amarradas ao próprio lead (bind com este item) para que
  // o card e o menu de ações só precisem receber `lead` — adicionar uma
  // ação nova não deve exigir tocar em toda a cadeia de componentes do card.
  // `dragHandleProps` só chega para os leads fixados (Leads.PinnedGroup.js);
  // para os demais é undefined e o header fica só clicável.
  const renderLead = (lead, dragHandleProps) => (
    <LeadsCard
      key={lead.id}
      lead={{
        ...lead,
        dragHandleProps,
        onAccept: () => onAccept(lead),
        onRelease: () => onRelease(lead),
        onClose: () => onClose(lead),
        onReopen: () => onReopen(lead),
        onTransfer: () => onTransfer(lead),
        onLeadDetails: () => onLeadDetails(lead),
        onOpenConversation: () => onOpenConversation(lead),
        onPin: () => onPin(lead),
        onUnpin: () => onUnpin(lead),
        onFavorite: () => onFavorite(lead),
        onUnfavorite: () => onUnfavorite(lead),
        onManageLists: () => onManageLists(lead),
      }}
      pendingAction={pendingActions[lead.uuid]}
      detailed={detailed}
    />
  );

  // O backend já devolve os fixados primeiro (ordering=pin_position), então o
  // split preserva a ordem — não há re-sort no cliente.
  const pinned = leads.filter((lead) => lead.isPinned);
  const rest = leads.filter((lead) => !lead.isPinned);

  return (
    <>
      <LeadsPinnedGroup
        leads={pinned}
        onReorder={onReorderPins}
        renderLead={renderLead}
      />
      {rest.map((lead) => renderLead(lead))}
      {hasNextPage && (
        <Box ref={observerTarget} sx={{ textAlign: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </>
  );
};

export default LeadsList;
